import { Avatar, Spinner, Tooltip } from 'flowbite-react';
import { get } from 'lodash';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { BiChevronRight } from 'react-icons/bi';
import { BsCheck } from 'react-icons/bs';
import { HiBriefcase, HiClock, HiCurrencyRupee, HiLocationMarker } from 'react-icons/hi';
import { IoIosCloseCircle } from 'react-icons/io';
import { useMutation, useQueryClient } from 'react-query';
import ReactQuill from 'react-quill';
import { useNavigate, useParams } from 'react-router-dom';
import { generatePositionTitle } from 'shared-utils';
import { editPosition } from 'src/apis/position';
import ReadMoreText from 'src/components/shared/ReadMoreText';
import { useToast } from 'src/context/ToastProvider';
import { generateCompanyLogoAvatarSrc } from 'src/utils/companyLogo';
import { PositionTitle, PositionType, TechStacksForPosition, UIPosition, WorkingMode } from 'types';

interface Props {
	data: UIPosition;
	description?: string;
	isLoading?: boolean;
	confirmLevelLoading?: boolean;
	setDisableBottomButtons?: React.Dispatch<React.SetStateAction<boolean>>;
}

const JobInfoCard = ({ data, description = '', isLoading, setDisableBottomButtons, confirmLevelLoading }: Props) => {
	const toast = useToast();
	const navigate = useNavigate();
	const queryClient = useQueryClient();
	const { companyId, positionId, jobId } = useParams();

	const ref = useRef<HTMLDivElement>(null);
	const [updatedJD, setUpdatedJD] = useState<string>('');
	const [showJDEditor, setShowJDEditor] = useState<boolean>(false);

	const [avatarImg, setAvatarImg] = useState<string>('');
	const [start, setStart] = useState<boolean>(false);

	const positionTitle = generatePositionTitle(data.title as PositionTitle);

	const generateAvatar = useCallback(async () => {
		const res = await generateCompanyLogoAvatarSrc(data?.company?.logo || '', data?.company?.url);
		setAvatarImg(res);
	}, [data?.company?.logo, data?.company?.url]);

	useEffect(() => {
		generateAvatar();
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [data?.company?.logo, data?.company?.url]);

	// Typewriter effect start
	let index = 0;
	let timer: any = null;

	const type = async () => {
		clearTimeout(timer);

		const typewriter = ref.current;

		if (!typewriter) return;

		if (index < description.length) {
			typewriter.innerHTML = description.slice(0, index);
			// + '<span className="blinking-cursor">|</span>';
			index++;
			timer = setTimeout(type, -1);
		} else {
			typewriter.innerHTML = description.slice(0, index);
		}
	};

	const editPositionFn = useMutation(['editPosition'], editPosition, {
		onSuccess() {
			queryClient.invalidateQueries(['getPositionById', data?._id]);
			setShowJDEditor(false);
			setDisableBottomButtons && setDisableBottomButtons(false);
		},
		onError(error: Error) {
			const errorMessage = get(error, ['response', 'data', 'message'], error.message);
			toast({
				type: 'error',
				title: errorMessage,
			});
		},
	});

	React.useEffect(() => {
		if (!showJDEditor && localStorage.getItem('typewriterId') !== data?._id && start && description) {
			type();
			setStart(false);
			localStorage.setItem('typewriterId', data?._id);
		} else if (!showJDEditor && description) {
			index = description.length;
			type();
		}
	}, [showJDEditor, isLoading, start, description]);
	// Typewriter effect end

	useEffect(() => {
		if (description.length && !isLoading) {
			setStart(true);
			setUpdatedJD(description);
		}
	}, [isLoading, description]);


	return (
		<div className="w-full border flex flex-col gap-4 border-[#9DBFF9] dark:border-[#3F3F3F] bg-[#f7f8fecc] rounded-[12px] dark:bg-[#1F2A37] py-2 px-4 mt-3 shadow-sm dark:shadow-none">
			<div className="w-full flex flex-col gap-2">
				{/* Top section */}
				<div className="flex gap-2">
					{/* Company Logo Div */}
					<div className="flex items-center">
						<Avatar
							img={avatarImg}
							rounded
							alt="avatar of Jese"
							className="position-avatar object-contain [&_img]:w-[32px] [&_img]:h-[32px] md:[&_img]:w-[42px] md:[&_img]:h-[42px]"
						/>
					</div>
					{/* Company Name / Rating / Position Name */}
					<div className="font-inter text-[#1C1C28] dark:text-[#F2F2F5] flex flex-col justify-center grow-2">
						<div className="flex flex-row justify-between items-center">
							<div className="text-[16px] md:text-[20px] font-inter font-[600] sm:font-[700]">
								{data.company.name.length > 14 ? (
									<Tooltip content={data.company.name} placement="top">
										{`${data.company.name.substring(0, 13)}...`}
									</Tooltip>
								) : (
									<>{data.company.name}</>
								)}
							</div>
						</div>
						<div className="text-[#28293D] dark:text-[#F2F2F5] sm:text-[14px] text-[16px] font-inter font-[400] md:font-[500]">
							{positionTitle.length > 29 ? (
								<Tooltip content={positionTitle} placement="top">
									{`${positionTitle.substring(0, 27)}...`}
								</Tooltip>
							) : (
								<>{positionTitle}</>
							)}
						</div>
					</div>
				</div>

				{/* Middle Section */}
				<div className="flex md:mb-2 sm:gap-x-4 mt-1 sm:gap-y-[6px] flex-col  md:gap-[6px]">
					{data?.workingMode === WorkingMode.INTERNSHIP_TO_FULL_TIME ? (
						<div className="bg-white mx-0 mt-0 px-[24px] md:px-[14px] py-[12px] rounded-[16px]">
							<div className="flex flex-col gap-[10px]">
								<div className="flex-1">
									<p className="text-[#7777E7] dark:text-[#a7cbff] text-sm font-inter font-[500]">
										Job Type: {data?.workingMode === 'InternshipToFullTime' ? 'Internship to Full-Time' : 'Internship'}
									</p>

									<div className="flex flex-wrap gap-x-[20%] md:gap-x-[16px] gap-y-[12px] pt-[7px]">
										<div className="flex items-center sm:text-[12px] text-[14px] text-[#555770] dark:text-[#E4E4EB]">
											<HiBriefcase className="text-[#00B7C4] dark:text-[#A9EFF2] w-[20px] h-[20px]" />
											<div className="ml-1 flex items-center gap-1">Internship</div>
										</div>

										<div>
											<div className="flex items-center sm:text-[12px] text-[14px] text-[#555770] dark:text-[#E4E4EB]">
												<HiClock className="text-[#00B7C4] dark:text-[#A9EFF2] w-[20px] h-[20px]" />
												<div className="ml-1">{data?.internshipDuration} months</div>
											</div>
										</div>

										{data?.internshipSalary?.max ? (
											<div>
												<div className="flex items-center sm:text-[12px] text-[14px] text-[#555770] dark:text-[#E4E4EB]">
													<HiCurrencyRupee className="text-[#00B7C4] dark:text-[#A9EFF2] w-[20px] h-[20px]" />
													<div className="ml-1">
														{`${
															data?.internshipSalary?.min === data?.internshipSalary?.max
																? data?.internshipSalary?.max
																: `${data?.internshipSalary?.min} - ${data?.internshipSalary?.max}`
														}`}{' '}
														K per month
													</div>
												</div>
											</div>
										) : null}

										<div className="flex items-center sm:text-[12px] text-[14px] text-[#555770] dark:text-[#E4E4EB]">
											<HiLocationMarker className="text-[#00B7C4] dark:text-[#A9EFF2] w-[20px] h-[20px]" />
											<div className="ml-1 flex items-center gap-1 flex-wrap">
												{data?.workLocation}
												<div className="font-inter font-[500] text-[15px] leading-[16px] text-[#8F90A6] dark:text-[#C7C9D9]">
													•
												</div>
												<span>{data?.positionType}</span>
											</div>
										</div>
									</div>
								</div>

								<div className="flex-1">
									<p className="text-[#7777E7] dark:text-[#a7cbff] text-sm font-inter font-[500]">
										Conversion to Full - Time{' '}
									</p>

									<div className="flex flex-wrap gap-x-[20%] md:gap-x-[16px] gap-y-[12px] pt-[7px]">
										<div className="flex items-center sm:text-[12px] text-[14px] text-[#555770] dark:text-[#E4E4EB]">
											<HiBriefcase className="text-[#00B7C4] dark:text-[#A9EFF2] w-[20px] h-[20px]" />
											<div className="ml-1 flex items-center gap-1">Full - time</div>
										</div>

										<div>
											<div className="flex items-center sm:text-[12px] text-[14px] text-[#555770] dark:text-[#E4E4EB]">
												<HiCurrencyRupee className="text-[#00B7C4] dark:text-[#A9EFF2] w-[20px] h-[20px]" />
												<div className="ml-1">
													{`${
														data?.salary?.min === data?.salary?.max
															? data?.salary?.max
															: `${data?.salary?.min} - ${data?.salary?.max}`
													}`}{' '}
													LPA
												</div>
											</div>
										</div>

										<div className="flex items-center sm:text-[12px] text-[14px] text-[#555770] dark:text-[#E4E4EB]">
											<HiLocationMarker className="text-[#00B7C4] dark:text-[#A9EFF2] w-[20px] h-[20px]" />
											<div className="ml-1 flex items-center gap-1 flex-wrap">
												{data?.workLocation}
												<div className="font-inter font-[500] text-[15px] leading-[16px] text-[#8F90A6] dark:text-[#C7C9D9]">
													•
												</div>
												<span>{data?.positionType}</span>
											</div>
										</div>
									</div>
								</div>
							</div>
						</div>
					) : (
						<>
							{/* Working Type / Experience */}
							<div className="flex flex-row gap-2 items-center">
								<div>
									<HiBriefcase className="text-[#00B7C4] w-[20px] h-[20px] dark:text-[#A9EFF2]" />
								</div>
								<div className="flex gap-1 items-center sm:text-[12px] text-[14px] text-[#555770] dark:text-[#E4E4EB]">
									<div>{data.workingMode}</div>
									<div className="font-inter font-[500] text-[24px] leading-[16px] text-[#8F90A6] dark:text-[#C7C9D9]">
										•
									</div>
									<div>
										{`${Math.floor(data.positionRequirement.workExperience.min)} Yrs ${
											Math.ceil(data.positionRequirement.workExperience?.min * 12) % 12
										} Mon - ${Math.floor(data.positionRequirement.workExperience.max)} Yrs ${
											Math.ceil(data.positionRequirement.workExperience?.max * 12) % 12
										} Mon `}
									</div>
								</div>
							</div>
							{/* Salary Package */}
							<div className="flex flex-row gap-2 items-center">
								<div>
									<HiCurrencyRupee className="text-[#00B7C4] w-[20px] h-[20px] dark:text-[#A9EFF2]" />
								</div>
								<div className="flex items-center sm:text-[12px] text-[14px] text-[#555770] dark:text-[#E4E4EB]">
									{data.salary.min} - {data.salary.max} LPA
								</div>
							</div>
							{/* Job Location / Working Mode */}
							<div className="flex flex-row gap-2 items-center">
								<div>
									<HiLocationMarker className="text-[#00B7C4] w-[20px] h-[20px] dark:text-[#A9EFF2]" />
								</div>
								<div className="flex gap-1 items-center sm:text-[12px] text-[14px] text-[#555770] dark:text-[#E4E4EB]">
									<div>{data.workLocations?.join(" | ")}</div>
									<div className="font-inter font-[500] text-[24px] leading-[16px] text-[#8F90A6] dark:text-[#C7C9D9]">
										•
									</div>
									<div>{data.positionType}</div>
								</div>
							</div>
							{/* Internship duration */}
							{data.workingMode === WorkingMode.INTERNSHIP && data.internshipDuration ? (
								<div className="flex flex-row gap-2 items-center">
									<div>
										<HiClock className="text-[#00B7C4] w-[20px] h-[20px] dark:text-[#A9EFF2]" />
									</div>
									<div className="flex gap-1 items-center sm:text-[12px] text-[14px] text-[#555770] dark:text-[#E4E4EB]">
										<div>
											{data.internshipDuration} {data.internshipDuration > 1 ? 'months' : 'month'}
										</div>
									</div>
								</div>
							) : null}
						</>
					)}
				</div>
			</div>

			{/* Skill Required */}
			<div className="block">
				<p className="text-[#28293D] dark:text-[#9DBFF9]">Technical Skills required</p>
				<div className="flex flex-wrap gap-3 mt-2">
					{data?.positionRequirement?.skillThresholds?.map(skillThreshold => {
						return (
							<div
								key={skillThreshold?.displaySkill}
								className="border border-[#5B8DEF] flex items-center gap-[4px] dark:border-[#C7D0F2] rounded-[5.2px] bg-[#E5F0FF] md:rounded-[6px] px-[12px] py-1 text-[12.133px] md:text-[14px] leading-[18.2px] text-[#28293D]"
							>
								{skillThreshold?.displaySkill}

								<div
									className="w-[12.133px] h-[12.133px] rounded-[100%] text-white"
									style={{ background: 'linear-gradient(147.14deg, #5B8DEF 6.95%, #0063F7 93.05%)' }}
								>
									<BsCheck className="w-[12.133px] h-[12.133px]" />
								</div>
							</div>
						);
					})}
					{data?.positionRequirement?.goodToHaveskills?.map(goodToHaveskill => {
						return (
							<div
								key={goodToHaveskill?.name}
								className="border border-[#5B8DEF] dark:border-[#C7D0F2] rounded-[5.2px] bg-[#E5F0FF] md:rounded-[6px] px-[12px] py-1 text-[12.133px] md:text-[14px] leading-[18.2px] text-[#28293D]"
							>
								{goodToHaveskill?.name}
							</div>
						);
					})}
				</div>
			</div>

			{/* Bond Details */}
			{data?.hasBond && data?.bondDetails ? (
				<div>
					<p className="text-[#28293D] dark:text-[#9DBFF9]">Bond Details</p>
					<div className=" prose dark:prose-invert text-sm mt-[10px]">
						<ReadMoreText work_description={data?.bondDetails} maxChars={data?.bondDetails?.length > 100 ? 200 : 50} />
					</div>
				</div>
			) : null}

			{/* Job Description */}
			<div className="block py-4 border rounded-[15px] border-[#6698FA] bg-white dark:border-0 dark:rounded-0 dark:bg-inherit">
				<div className="flex justify-between px-3 items-center">
					<p className="text-[#28293D] dark:text-[#9DBFF9] text-base md:text-lg font-[500] font-inter">
						Job description
					</p>

					<button
						disabled={confirmLevelLoading}
						className="flex gap-1 items-center cursor-pointer justify-end text-[#7777E7] dark:text-[#6698FA] text-base md:text-lg font-[500] font-inter"
						onClick={() => {
							if (editPositionFn.isLoading) {
								return;
							}
							// if (companyId) navigate(`/edit-position/${companyId}/${data._id}`);
							// else navigate(`/edit-position/${data._id}`);
							if (showJDEditor && updatedJD !== description) {
								editPositionFn.mutate({
									id: data?._id,
									payload: {
										title: data.title as PositionTitle,
										techStack: data.techStack as TechStacksForPosition,
										requiredBTech: data.requiredBTech,
										experience: data.positionRequirement.workExperience,
										goodToHaveSkillNames: data.positionRequirement?.goodToHaveskills?.map(skill => skill.name) || [],
										requiredSkillNames:
											data.positionRequirement?.skillThresholds?.map(skillthreshold => skillthreshold.displaySkill) ||
											[],
										location: data.workLocation,
										positionType: data.positionType as PositionType,
										salaryRange: data.salary,
										description: updatedJD || data.description,
										numberOfRounds: data.numberOfRounds ?? 3,
										workingMode: data.workingMode,
										vacancy: data.vacancy,
									},
								});
								setDisableBottomButtons && setDisableBottomButtons(true);
							} else {
								setDisableBottomButtons && setDisableBottomButtons(prev => !prev);
								setShowJDEditor(prev => !prev);
							}
						}}
					>
						{editPositionFn.isLoading ? (
							<Spinner />
						) : (
							<span className="hover:underline">{showJDEditor ? 'Save' : 'Edit'}</span>
						)}{' '}
						{showJDEditor ? (
							<button
								disabled={editPositionFn?.isLoading}
								className="disabled:cursor-not-allowed disabled:opacity-[0.6]"
								onClick={() => {
									if (!editPositionFn?.isLoading) {
										setShowJDEditor(true);
									}
								}}
							>
								<IoIosCloseCircle className="text-[20px] md:text-[24px] ml-2 text-inherit inline-block" />
							</button>
						) : (
							<BiChevronRight className="text-[20px] md:text-[34px] text-inherit inline-block" />
						)}
					</button>
				</div>
				<div className="flex">
					{showJDEditor ? (
						<div className={`w-full px-3 mt-2 ${editPositionFn.isLoading ? 'opacity-[0.5]' : ''}`}>
							{/* Job Description Editor */}
							<ReactQuill
								readOnly={editPositionFn.isLoading}
								className="w-full rounded-[12px] border [&_.ql-toolbar]:!border-[0px] [&_.ql-toolbar]:!border-b-[1px] [&_.ql-container]:max-h-[300px] [&_.ql-container]:overflow-auto [&_.ql-container]:!border-[0px] dark:text-white bg-gray-50 dark:bg-[#374151]"
								value={updatedJD}
								onChange={data => {
									if (data) {
										setUpdatedJD(data);
									}
								}}
							/>
						</div>
					) : (
						<div>
							<div ref={ref} className=" prose dark:prose-invert text-sm px-4 mt-[10px]"></div>
						</div>
					)}
				</div>
			</div>

			{/* <div className="md:my-1 text-[#6698FA] text-[18px] flex justify-end">
				<p
					className="flex gap-1 items-center cursor-pointer justify-end"
					onClick={() => {
						navigate(`/edit-position/${data._id}`);
					}}
				>
					EDIT <BiChevronRight className="text-[34px] text-[#6698FA] inline-block" />
				</p>
			</div> */}
		</div>
	);
};

export default JobInfoCard;
